// Channel display metadata for the News Watch Studio — labels, brand colors,
// and the grouped ordering used in the hub's channel picker. Keyed by the
// run-path `ContentChannel` ids from @traaker/shared.

import type { ContentChannel, ContentFormat, ContentPattern, ContentObjective } from '@traaker/shared';

export const CHANNEL_LABEL: Record<ContentChannel, string> = {
  blog_seo: 'Blog SEO',
  landing_page: 'Landing page',
  medium: 'Medium',
  linkedin_post: 'LinkedIn post',
  linkedin_article: 'LinkedIn article',
  x_thread: 'X thread',
  x_post: 'X post',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok / Reels',
  youtube: 'YouTube (script)',
  threads: 'Threads',
  reddit: 'Reddit post',
  quora: 'Quora',
  newsletter: 'Newsletter',
};

export const CHANNEL_COLOR: Record<ContentChannel, string> = {
  blog_seo: '#334155',
  landing_page: '#0EA5A0',
  medium: '#000000',
  linkedin_post: '#0A66C2',
  linkedin_article: '#0A66C2',
  x_thread: '#000000',
  x_post: '#000000',
  instagram: '#E1306C',
  facebook: '#1877F2',
  tiktok: '#000000',
  youtube: '#FF0000',
  threads: '#000000',
  reddit: '#FF4500',
  quora: '#B92B27',
  newsletter: '#6B7280',
};

// NOTE: `label`/`hint`/`tip` values below are i18n KEYS (prefix `newsChannels.`),
// not display strings — the consuming component resolves them with `t(...)`.
export const CHANNEL_GROUPS: { label: string; channels: ContentChannel[] }[] = [
  { label: 'newsChannels.group.webSeo', channels: ['blog_seo', 'landing_page', 'medium'] },
  { label: 'newsChannels.group.proSocial', channels: ['linkedin_post', 'linkedin_article', 'x_thread', 'x_post'] },
  { label: 'newsChannels.group.consumerSocial', channels: ['instagram', 'facebook', 'tiktok', 'youtube', 'threads'] },
  { label: 'newsChannels.group.communityEmail', channels: ['reddit', 'quora', 'newsletter'] },
];

export const FORMAT_OPTIONS: { value: ContentFormat; label: string; hint: string }[] = [
  { value: 'micro', label: 'newsChannels.format.micro.label', hint: 'newsChannels.format.micro.hint' },
  { value: 'court', label: 'newsChannels.format.court.label', hint: 'newsChannels.format.court.hint' },
  { value: 'standard', label: 'newsChannels.format.standard.label', hint: 'newsChannels.format.standard.hint' },
  { value: 'long', label: 'newsChannels.format.long.label', hint: 'newsChannels.format.long.hint' },
];

export const PATTERN_OPTIONS: { value: ContentPattern; label: string; tip: string }[] = [
  { value: 'storytelling', label: 'newsChannels.pattern.storytelling.label', tip: 'newsChannels.pattern.storytelling.tip' },
  { value: 'listicle', label: 'newsChannels.pattern.listicle.label', tip: 'newsChannels.pattern.listicle.tip' },
  { value: 'guide', label: 'newsChannels.pattern.guide.label', tip: 'newsChannels.pattern.guide.tip' },
  { value: 'opinion', label: 'newsChannels.pattern.opinion.label', tip: 'newsChannels.pattern.opinion.tip' },
];

export const OBJECTIVE_OPTIONS: { value: ContentObjective; label: string; hint: string; icon: string }[] = [
  { value: 'acquisition', label: 'newsChannels.objective.acquisition.label', hint: 'newsChannels.objective.acquisition.hint', icon: '🧲' },
  { value: 'authority', label: 'newsChannels.objective.authority.label', hint: 'newsChannels.objective.authority.hint', icon: '🎓' },
  { value: 'conversion', label: 'newsChannels.objective.conversion.label', hint: 'newsChannels.objective.conversion.hint', icon: '💰' },
  { value: 'image', label: 'newsChannels.objective.image.label', hint: 'newsChannels.objective.image.hint', icon: '✨' },
];

/** Momentum band → chip label i18n key + color. Resolve `label` with `t(...)`. */
export const MOMENTUM_META: Record<string, { label: string; color: string }> = {
  forte: { label: 'newsChannels.momentum.forte', color: '#10B981' },
  moyenne: { label: 'newsChannels.momentum.moyenne', color: '#F59E0B' },
  faible: { label: 'newsChannels.momentum.faible', color: '#9AA' },
  evergreen: { label: 'newsChannels.momentum.evergreen', color: '#6B7280' },
};
